import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { adminApi, getErrorMessage } from "@/lib/api";
import { DecisionPill } from "../assess/parts";
import { ApproveForm } from "./ApproveForm";
import { ApprovalResult } from "./ApprovalResult";
import { EmailPreview } from "./EmailPreview";

export function ApproveDrawer({ target, onClose, onChanged }) {
  const [p, setP] = useState(null);
  const [a, setA] = useState(target.approval?.steps ? target.approval : null);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState(null);
  const loadPreview = useCallback(() => adminApi.get(`/admin/wi/approvals/${target.claim_id}/preview`, { timeout: 60000 })
    .then(({ data }) => { setP(data); if (data.approval) setA(data.approval); }).catch((e) => setErr(getErrorMessage(e))), [target.claim_id]);
  useEffect(() => { if (!a) loadPreview(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const refreshApproval = useCallback(async () => {
    if (!a) return;
    const { data } = await adminApi.get("/admin/wi/approvals").catch(() => ({ data: { rows: [] } }));
    const fresh = data.rows.find((r) => r.id === a.id);
    if (fresh) setA(fresh);
    onChanged?.();
  }, [a, onChanged]);
  const retry = async () => {
    setBusy(true);
    try { const { data } = await adminApi.post(`/admin/wi/approvals/${a.id}/retry`, null, { timeout: 120000 }); setA(data); onChanged?.(); data.status === "done" ? toast.success(`Approved as ${data.otma}`) : toast.error(data.error); }
    catch (e) { toast.error(getErrorMessage(e)); } finally { setBusy(false); }
  };
  const c = p?.claim || a || target;
  return (
    <Sheet open onOpenChange={(v) => !v && onClose()}>
      <SheetContent side="right" className="bg-[#0A0A0A] border-neutral-800 text-neutral-200 w-full sm:max-w-3xl overflow-y-auto" data-testid="wi-approve-drawer">
        <SheetHeader className="mb-4">
          <SheetTitle className="font-heading text-white text-left flex items-center gap-3 flex-wrap">{a ? "Approval" : "Review & approve"} <span className="font-mono text-sm text-neutral-400">{c.chassis}</span>
            {!a && target.ai && <DecisionPill decision={target.ai.decision} />}</SheetTitle>
          <SheetDescription className="text-left text-neutral-500">{[c.customer, c.model, c.dealer].filter(Boolean).join(" · ")}</SheetDescription>
        </SheetHeader>
        {a ? <ApprovalResult a={a} busy={busy} onRetry={retry} onViewEmail={(e) => setEmail(e.id)} mondayUrl={a.monday_url || p?.monday_url} />
          : err ? <p className="text-sm text-red-400" data-testid="wi-approve-error">{err}</p>
          : !p ? <div className="py-16 text-center text-sm text-neutral-500" data-testid="wi-approve-loading">Checking the claim on Monday.com…</div>
          : <ApproveForm p={p} onDone={(d) => { setA(d); onChanged?.(); }} />}
        {email && <EmailPreview emailId={email} onClose={() => setEmail(null)} onChanged={refreshApproval} />}
      </SheetContent>
    </Sheet>
  );
}
